import {
  createContext,
  Dispatch,
  FC,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useGetAdvancedSearchLocations } from "../../../../components/advanced-search/hooks/useGetAdvancedSearchLocations";
import { Item } from "../../../../components/ui-kit";
import { useUserContext } from "../../../../context/UserContextProvider";
import {
  BrandApiType,
  CustomerApiType,
  LocationApiType,
  ProductApiType,
  ProductVariantApiType,
} from "../../../../util/types";
import {
  IInfiniteScrollFilters,
  useInfiniteScrollProductsList,
} from "../hooks/useInfiniteScrollProductsList";
import {
  CURRENCIES,
  CurrencyCodes,
  OrderDeliveryType,
  OrderProductPriceType,
  ProductStatus,
} from "@fena/toolkit-types";
import { useCompanyStore } from "../../../../stores/useCompanyStore";
import { CurrencyItem } from "../../../../constants/currencies";
import api from "../../../../util/api";
import { useLocation, useNavigate } from "react-router-dom";

interface IOrderGenerationContext {
  orderGenerationData: ProductApiType[];
  setOrderGenerationFilters: (
    value:
      | ((prevState: IInfiniteScrollFilters) => IInfiniteScrollFilters)
      | IInfiniteScrollFilters
  ) => void;
  onPaginationChange: (pageValue: number, limitValue: number) => void;
  onStatusChange: (status: ProductStatus) => void;
  onCategoryChange: (category: string) => void;
  onSearchChange: (category: string) => void;
  setBrandFilter: (brand: BrandApiType[]) => void;
  loading: boolean;
  hasNextPage?: boolean;
  getProductList: () => void;
  isMobile: boolean;
  showMobileCart: boolean;
  setShowMobileCart: (val: boolean) => void;
  total: number;
  setTotal: (val: number) => void;
  showSelectPayMethod: boolean;
  setShowSelectPayMethod: (val: boolean) => void;

  currentCurrency: CurrencyItem;
  setCurrentCurrency: Dispatch<SetStateAction<CurrencyItem>>;

  // Cart control props
  cart: ProductVariantCartState[];
  updateCart: (products: ProductVariantCartState[]) => void;
  // orderFromDraft?: OrderApiType;
  // setOrderFromDraft: Dispatch<SetStateAction<OrderApiType | undefined>>;
  onAddProductVariantToCart: (
    selectedProductVariant: ProductVariantCartState
  ) => void;
  onProductCountChange: (val: number, productVariantId: string) => void;
  onDeleteProduct: (productVariantId: string) => void;
  isExpandedCart: boolean;
  setIsExpandedCart: Dispatch<SetStateAction<boolean>>;
  showDeliveryDetails: boolean;
  setShowDeliveryDetails: Dispatch<SetStateAction<boolean>>;
  productsPriceType: OrderProductPriceType;
  setProductsPriceType: (val: OrderProductPriceType) => void;
  stockLocations: LocationApiType[];
  stockLocation: Item;
  setStockLocation: Dispatch<SetStateAction<Item>>;
  // End Cart control props

  // Delivery details
  customer?: CustomerApiType;
  setCustomer: (customer?: CustomerApiType) => void;
  deliveryType: string;
  setDeliveryType: (val: OrderDeliveryType) => void;
  deliveryDetailsPickUpPointId?: string;
  setDeliveryDetailsPickUpPointId: (val?: string) => void;
  // End delivery details
}

export interface ProductVariantCartState {
  productVariant: ProductVariantApiType;
  parentProduct: ProductApiType;
  currentCurrency: CurrencyCodes;
  qty: number;
}

const OrderGenerationContext = createContext<IOrderGenerationContext>({
  orderGenerationData: [],
  loading: false,
  hasNextPage: false,
  setOrderGenerationFilters: () => null,
  onPaginationChange: () => null,
  onStatusChange: () => null,
  onCategoryChange: () => null,
  onSearchChange: () => null,
  setBrandFilter: () => null,
  getProductList: () => null,
  isMobile: false,
  showMobileCart: true,
  setShowMobileCart: () => null,
  total: 0,
  setTotal: () => null,
  showSelectPayMethod: false,
  setShowSelectPayMethod: () => null,

  currentCurrency: {
    label: "label",
    currencyCode: CurrencyCodes.POUND_STERLING,
    countryCode: "GB",
    symbol: "symbol",
  },
  setCurrentCurrency: () => {},

  stockLocation: { label: "All locations" },
  setStockLocation: () => {},

  // Cart control props
  cart: [],
  updateCart: () => null,
  // orderFromDraft: undefined,
  // setOrderFromDraft: () => null,
  onAddProductVariantToCart: () => null,
  onProductCountChange: () => null,
  onDeleteProduct: () => null,
  isExpandedCart: false,
  setIsExpandedCart: () => null,
  showDeliveryDetails: false,
  setShowDeliveryDetails: () => null,
  productsPriceType: OrderProductPriceType.RETAIL,
  setProductsPriceType: () => null,
  stockLocations: [],

  // Delivery details
  customer: {} as CustomerApiType,
  setCustomer: () => null,
  deliveryType: OrderDeliveryType.DELIVERY_ADDRESS,
  setDeliveryType: () => null,
  deliveryDetailsPickUpPointId: "",
  setDeliveryDetailsPickUpPointId: () => null,
});

export const OrderGenerationContextProvider: FC<{ children: ReactElement }> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { companySettings } = useCompanyStore();
  const { locations } = useGetAdvancedSearchLocations({
    type: "shipping",
    ignorePoliciesCheck: true,
  });
  const defaultLocation = locations?.find((l) => l.isDefault);

  const defaultCurrency =
    CURRENCIES[
      companySettings?.currencies?.defaultCurrency ??
        CurrencyCodes.POUND_STERLING
    ];
  const [filters, setFilters] = useState<IInfiniteScrollFilters>({});
  const [productsPriceType, setProductsPriceType] =
    useState<OrderProductPriceType>(OrderProductPriceType.RETAIL);
  const [stockLocation, setStockLocation] = useState<Item>({
    label: defaultLocation?.name ?? "",
    value: defaultLocation?._id,
  });
  const [stockLocations, setStockLocations] = useState<LocationApiType[]>([]);

  const [currentCurrency, setCurrentCurrency] = useState<CurrencyItem>({
    label: defaultCurrency.name,
    currencyCode:
      (Object.keys(CURRENCIES).find(
        (c) => c === companySettings?.currencies?.defaultCurrency
      ) as CurrencyCodes) ?? CurrencyCodes.POUND_STERLING,
    countryCode: defaultCurrency.countryCode,
    symbol: defaultCurrency.symbol,
  });

  const [showMobileCart, setShowMobileCart] = useState(true);
  const [total, setTotal] = useState<number>(0);
  const [showSelectPayMethod, setShowSelectPayMethod] = useState(false);

  const isMobile = useMemo(() => {
    return window.innerWidth <= 576;
  }, [window.innerWidth]);

  const {
    productsData,
    hasNextPage,
    onPaginationChange,
    onStatusChange,
    onCategoryChange,
    onSearchChange,
    onBrandFilterChange,
    onPriceTypeFilterChange,
    loading,
    getProductList,
  } = useInfiniteScrollProductsList({
    ...filters,
    currency: currentCurrency.currencyCode,
    location: stockLocation.value,
  });

  const handlePriceType = (val: OrderProductPriceType) => {
    setProductsPriceType(val);
    onPriceTypeFilterChange(val);
  };

  // Cart control context
  const [cart, setCart] = useState<ProductVariantCartState[]>([]);
  const [isExpandedCart, setIsExpandedCart] = useState<boolean>(false);
  const [showDeliveryDetails, setShowDeliveryDetails] = useState(false);

  const onAddProductVariantToCart = (
    selectedProductVariant: ProductVariantCartState
  ) => {
    if (cart.length === 0 && !isMobile) {
      setIsExpandedCart(true);
    }
    const index = cart.findIndex(
      (pv) =>
        pv.productVariant._id === selectedProductVariant.productVariant._id
    );
    if (index === -1) {
      setCart([{ ...selectedProductVariant }, ...cart]);
    } else {
      const newCart = [...cart];
      newCart[index].qty = selectedProductVariant.qty;
      setCart(newCart);
    }
  };

  const onProductCountChange = (val: number, productVariantId: string) => {
    setCart((prevList) => {
      const index = prevList.findIndex(
        (pv) => pv.productVariant._id === productVariantId
      );
      if (index !== -1) {
        return prevList.map((pv, i) =>
          i === index ? { ...pv, qty: val } : pv
        );
      } else {
        return prevList;
      }
    });
  };

  const onDeleteProduct = (productVariantId: string) => {
    setCart((prevList) => {
      const index = prevList.findIndex(
        (pv) => pv.productVariant._id === productVariantId
      );
      if (index !== -1) {
        return prevList.filter(
          (pv) => pv.productVariant._id !== productVariantId
        );
      } else {
        return prevList;
      }
    });
  };
  // END Cart control context

  // Delivery details context
  const [customer, setCustomer] = useState<CustomerApiType>();
  const [deliveryType, setDeliveryType] = useState(
    OrderDeliveryType.DELIVERY_ADDRESS
  );
  const [deliveryDetailsPickUpPointId, setDeliveryDetailsPickUpPointId] =
    useState<string>();
  // End delivery details context

  const clearLocationState = () => {
    navigate(location.pathname, {
      replace: true,
      state: { customerId: undefined, isWholesale: location.state.isWholesale },
    });
  };

  const setCustomerFunction = async (customerId?: string) => {
    if (customerId) {
      const res = await api.getCustomer(customerId);
      setCustomer(res.data);
      clearLocationState();
    }
  };

  useEffect(() => {
    if (location.state?.customerId) {
      setCustomerFunction(location.state.customerId);
    }
  }, [location.state?.customerId, location.state?.isWholesale]);

  useEffect(() => {
    const assignedLocationIds =
      user?.selectedCompanyMembership?.locations
        ?.filter((l) => l.type === "shipping")
        ?.map((l) => l._id) ?? [];

    if (!assignedLocationIds || assignedLocationIds.length === 0) {
      setStockLocations(locations);
      setStockLocation({
        label: defaultLocation?.name ?? "",
        value: defaultLocation?._id,
      });
    } else {
      const assignedLocations =
        locations?.filter((location) =>
          assignedLocationIds?.includes(location._id)
        ) ?? [];

      const foundDefaultLocation = assignedLocations.find((l) => l.isDefault);
      setStockLocations(assignedLocations);
      setStockLocation(
        foundDefaultLocation
          ? {
              label: foundDefaultLocation?.name ?? "",
              value: foundDefaultLocation?._id,
            }
          : {
              label: assignedLocations?.[0]?.name ?? "",
              value: assignedLocations?.[0]?._id,
            }
      );
    }
  }, [locations, defaultLocation, user]);

  return (
    <OrderGenerationContext.Provider
      value={{
        orderGenerationData: productsData,
        loading,
        hasNextPage,
        onPaginationChange,
        onStatusChange,
        onCategoryChange,
        onSearchChange,
        setBrandFilter: onBrandFilterChange,
        setOrderGenerationFilters: setFilters,
        getProductList,
        isMobile,
        showMobileCart,
        setShowMobileCart,
        total,
        setTotal,
        showSelectPayMethod,
        setShowSelectPayMethod,

        currentCurrency,
        setCurrentCurrency,

        // Cart control props
        cart,
        updateCart: setCart,
        // orderFromDraft,
        // setOrderFromDraft,
        onAddProductVariantToCart,
        onProductCountChange,
        onDeleteProduct,
        isExpandedCart,
        setIsExpandedCart,
        showDeliveryDetails,
        setShowDeliveryDetails,
        productsPriceType,
        setProductsPriceType: handlePriceType,
        stockLocations,
        stockLocation,
        setStockLocation,
        // End Cart control props

        // Delivery details
        customer,
        setCustomer,
        deliveryType,
        setDeliveryType,
        setDeliveryDetailsPickUpPointId,
        deliveryDetailsPickUpPointId,
        // End of delivery details
      }}
    >
      {children}
    </OrderGenerationContext.Provider>
  );
};

export const useOrderGenerationContext = () => {
  return useContext(OrderGenerationContext);
};
